/**
 * Helper centralizzati per la formattazione dei numeri e importi monetari in tutta l'applicazione.
 * Regola generale: separatore delle migliaia sempre con il punto (.) secondo lo standard italiano it-IT.
 */

/**
 * Formatta un numero intero o decimale con separatore delle migliaia (punto).
 * Es: 1000 -> "1.000", 1250000 -> "1.250.000", 1250.5 -> "1.250,5"
 * 
 * @param {number|string} value - Valore da formattare
 * @param {object} options - Opzioni Intl.NumberFormat
 * @returns {string}
 */
export function formatNumber(value, options = {}) {
  if (value === null || value === undefined || value === "") return "-";
  const num = typeof value === "number" ? value : Number(value);
  if (isNaN(num)) return String(value);

  const formatted = new Intl.NumberFormat("it-IT", {
    useGrouping: true,
    maximumFractionDigits: 2,
    ...options,
  }).format(num);

  // Assicura il punto separatore anche in ambienti JS con configurazioni ICU minime
  if (!formatted.includes(".") && Math.abs(num) >= 1000) {
    const parts = formatted.split(",");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return parts.join(",");
  }

  return formatted;
}

/**
 * Formatta un importo in Euro con il punto come separatore delle migliaia.
 * Es: 1000 -> "€1.000", 1250.50 -> "€1.250,50"
 *
 * @param {number|string} value - Valore da formattare
 * @param {object} options - Opzioni personalizzate (es. decimals: 0 o 2, withSymbol: true/false, suffix: true/false)
 * @returns {string}
 */
export function formatCurrency(value, options = {}) {
  if (value === null || value === undefined || value === "") return options.suffix ? "0 €" : "€0";
  const num = typeof value === "number" ? value : Number(value);
  if (isNaN(num)) return String(value);

  const { decimals, withSymbol = true, suffix = false } = options;

  const formatOpts = {
    useGrouping: true,
    minimumFractionDigits: decimals !== undefined ? decimals : (num % 1 !== 0 ? 2 : 0),
    maximumFractionDigits: decimals !== undefined ? decimals : 2,
  };

  let formatted = new Intl.NumberFormat("it-IT", formatOpts).format(num);

  if (!formatted.includes(".") && Math.abs(num) >= 1000) {
    const parts = formatted.split(",");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    formatted = parts.join(",");
  }

  if (!withSymbol) return formatted;
  if (suffix) return `${formatted} €`;
  return `€${formatted}`;
}

/**
 * Alias per compatibilità
 */
export const fmtEuro = formatCurrency;
